/**
 * SalesContacts.tsx — Sales / Contacts table with Portal drawers
 * Design: Golden Stack — table list, right-side full-height drawer for create & detail
 *
 * P1: colors/sizes flow from src/theme/tokens.ts (canonical P1 design system).
 * Off-scale text-12/text-13 stay arbitrary by the GATE 0-A map.
 */
import { useState, useEffect } from 'react'
import { createPortal } from 'react-dom'
import {
  Plus, Search, Download, Linkedin, CheckCircle2, Circle,
  MoreHorizontal, X, User, Mail, Phone, Building2, Briefcase,
  Link2, Tag, MapPin, Layers, Pencil, ChevronUp, ChevronDown, ArrowUpDown
} from 'lucide-react'
import { SectionTitle, Avatar, LoadingState, ErrorState } from '../../DashboardShared'
import type { SalesContact } from '../../types'
import type { SalesMatchCandidate } from '../../../../api/types'
import { useAsync, fetchContacts, fetchContact, createContact, updateContact, fetchSalesMatches, candidatesFrom, fetchDirectory } from '../../api'
import { AdvisoryChooser, RefusalChooser, type CandidateAction } from './DuplicateChooser'
import { SalesAuditStrip } from './SalesAuditStrip'
import { OwnerSelect } from './OwnerSelect'
import { AccountField } from './AccountField'
import type { AccountPick } from './AccountPicker'
import { SOURCE_OPTIONS } from './vocab'
import {
  CATEGORY_OPTIONS, SEGMENT_OPTIONS, draftFromContact, contactEditDiff, contactEditSavable,
  type ContactEditDraft,
} from './contactEdit'
import { apiErrorMessage } from '../../../../api/client'
import { toast } from 'sonner'
import { accent, overlay, statusContact } from '../../../../theme/tokens'
import { useSavedViews } from './useSavedViews'
import { CONTACTS_SCHEMA, type SortDir } from './savedViews'
import { SavedViewsBar } from './SavedViewsBar'
import { InlinePicklist } from './InlinePicklist'
import {
  CONTACT_STATUS_OPTIONS, ownerOptions, ownerLabel, ownerCellText, statusLabel, statusChange, ownerChange,
  matchesContactsFilters, changeSummary, type PickOption,
} from './inlineEdit'

// ── Status config ─────────────────────────────────────────────────────────────

const STATUS_CONFIG = {
  new:         { label: 'New',         bg: statusContact.new.bg,         text: statusContact.new.text },
  contacted:   { label: 'Contacted',   bg: statusContact.contacted.bg,   text: statusContact.contacted.text },
  qualified:   { label: 'Qualified',   bg: statusContact.qualified.bg,   text: statusContact.qualified.text },
  unqualified: { label: 'Unqualified', bg: statusContact.unqualified.bg, text: statusContact.unqualified.text },
}

// CATEGORY_OPTIONS / SEGMENT_OPTIONS moved to contactEdit.ts (Slice E) so the
// create drawer, the detail drawer's editor and the pure diff share one list.

function ContactStatusBadge({ status }: { status: SalesContact['status'] }) {
  const cfg = STATUS_CONFIG[status]
  return (
    <span className="k-badge" style={{ background: cfg.bg, color: cfg.text }}>
      <span className="k-badge-dot" />{cfg.label.toUpperCase()}
    </span>
  )
}

function Field({ label, icon, children }: { label: string; icon: React.ReactNode; children: React.ReactNode }) {
  return (
    <div>
      <label className="flex items-center gap-2 text-12 font-semibold text-ink-body mb-2">
        <span className="text-ink-faint">{icon}</span>{label}
      </label>
      {children}
    </div>
  )
}

/** §25 L-7: the linked account, shown wherever the id travels — a chip that
 *  opens the account page when the host can, the name alone when it cannot,
 *  and the honest "Unlinked" state otherwise (never a blank that reads as
 *  "no company"). The chip stops the row click: opening an account is not
 *  opening the contact. */
function AccountLink({ contact, onOpenAccount, size = 'text-11', withIcon = false }: {
  contact: SalesContact
  onOpenAccount?: (accountId: string) => void
  size?: 'text-11' | 'text-13'
  /** The card's row carries the mark; the LIST chip is text only, so its
   *  11 px word never out-measures the company text's 13 px word above it —
   *  the icon + gap once widened the Company column by 4 px and tripped the
   *  D3 no-horizontal-overflow guard on the contacts table. */
  withIcon?: boolean
}) {
  if (!contact.accountId) return <span data-testid="account-link" className={`${size} text-ink-faint`}>Unlinked</span>
  const name = contact.accountName ?? contact.company
  const mark = withIcon ? <Building2 size={11} /> : null
  if (!onOpenAccount) return <span data-testid="account-link" className={`${size} text-ink-secondary inline-flex items-center gap-1`}>{mark}{name}</span>
  return (
    <button type="button" title="Open account" data-testid="account-link"
      onClick={e => { e.stopPropagation(); onOpenAccount(contact.accountId!) }}
      className={`${size} text-accent inline-flex items-center gap-1 text-left hover:underline`}>
      {mark}{name}
    </button>
  )
}

function InfoRow({ icon, label, value }: { icon: React.ReactNode; label: string; value: string }) {
  return (
    <div className="flex items-start gap-3 py-3 border-b border-edge-row last:border-0">
      <div className="w-8 h-8 rounded-control bg-surface-input flex items-center justify-center text-ink-faint flex-shrink-0">{icon}</div>
      <div className="min-w-0">
        <div className="text-11 text-ink-muted font-medium mb-1">{label}</div>
        <div className="text-13 text-ink-primary font-medium truncate">{value || '—'}</div>
      </div>
    </div>
  )
}

// ── New Contact Drawer ────────────────────────────────────────────────────────
// Exported: the Inbound Leads tab's "New Lead" button is this same create
// surface re-presented (§8.B — manual intake creates a status-new contact,
// which IS a queued lead under L8-1). One drawer, two entry points.

interface NewContactDrawerProps {
  onClose: () => void
  onCreated: (contact: SalesContact) => void
  /** C3b (B1.2): what "Open existing contact" does on a same-email candidate.
   *  The Contacts page opens that contact's detail (and closes this drawer); a
   *  host with no contact surface of its own (the Inbound Leads tab) leaves it
   *  undefined and the row stays informational — there is no merge to offer
   *  (P1-4), so "open it" and "create anyway" are the only honest actions. */
  onOpenExisting?: (contactId: string) => void
}

export function NewContactDrawer({ onClose, onCreated, onOpenExisting }: NewContactDrawerProps) {
  const [name, setName]       = useState('')
  const [email, setEmail]     = useState('')
  const [phone, setPhone]     = useState('')
  // §25 L-1: Company is the AccountPicker (pick-only, clearable) — the text
  // IS the company, the pick is the link; no pick, no link (P1-1).
  const [account, setAccount] = useState<AccountPick>({ accountId: '', company: '' })
  const [title, setTitle]     = useState('')
  const [linkedin, setLinkedin] = useState('')
  const [location, setLocation] = useState('')
  const [category, setCategory] = useState<'university' | 'college' | 'welfare-to-work'>('university')
  const [prospectSegment, setProspectSegment] = useState<'current' | 'future'>('current')
  const [busy, setBusy] = useState(false)
  // Close on Escape
  useEffect(() => {
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }
    document.addEventListener('keydown', handler)
    return () => document.removeEventListener('keydown', handler)
  }, [onClose])

  // C3b advisory pre-flight (B1.3): EMAIL ONLY — exact normalised email is the
  // one rule on this path. A contact's company text mints no account, so the
  // trigram rule does not apply here and account rows would be noise the form
  // cannot act on. Queried on BLUR of the Email field, never per keystroke
  // (the C2.2 discipline) and not at all while the email is blank — an
  // untouched drawer shows no tile. Advisory (C-2): it never gates Create.
  const [matchEmail, setMatchEmail] = useState('')
  const matchesQ = useAsync(
    () => (matchEmail === '' ? Promise.resolve([] as SalesMatchCandidate[]) : fetchSalesMatches({ email: matchEmail })),
    [matchEmail],
  )
  const candidates = matchesQ.data ?? []
  // C3b refusal: the server's 409 with candidates (C3's write-path guard). Set
  // only from a failed save, cleared on the next save and when the typed email
  // changes (it was about the previous address).
  const [refusal, setRefusal] = useState<{ message: string; candidates: SalesMatchCandidate[] } | null>(null)
  // What a row offers (DuplicateChooser): a same-email CONTACT is a real
  // alternative on this path, so it opens — when the host can open one.
  const actionFor = (c: SalesMatchCandidate): CandidateAction | undefined => (
    c.type === 'contact' && onOpenExisting
      ? { label: 'Open existing contact', onAct: () => onOpenExisting(c.id) }
      : undefined
  )

  async function handleSave(createAnyway: boolean) {
    if (!name.trim()) { toast.error('Full name is required'); return }
    setBusy(true)
    setRefusal(null)
    try {
      const created = await createContact({
        fullName: name.trim(),
        email: email.trim() || undefined,
        phone: phone.trim() || undefined,
        company: account.company.trim() || undefined,
        // The pick rides ONLY when made; an absent key is the normal create.
        ...(account.accountId ? { accountId: account.accountId } : {}),
        title: title.trim() || undefined,
        linkedInUrl: linkedin.trim() || undefined,
        location: location.trim() || undefined,
        category,
        prospectSegment,
        // The override travels only when true — an absent key is the normal
        // create, so a server without the refusal sees the old body unchanged.
        ...(createAnyway ? { createAnyway: true } : {}),
      })
      onCreated(created)
      toast.success('Prospect created')
      onClose()
    } catch (e) {
      const refused = candidatesFrom(e)
      if (refused) {
        setRefusal({ message: apiErrorMessage(e, 'The server refused this contact as a possible duplicate.'), candidates: refused })
      } else {
        toast.error(apiErrorMessage(e, 'Failed to create prospect'))
      }
      setBusy(false)
    }
  }

  const inputCls = "w-full px-4 py-3 bg-surface-control border border-edge-control rounded-control text-13 text-ink-body placeholder:text-ink-faint transition-all"

  return createPortal(
    <div style={{ position: 'fixed', inset: 0, zIndex: 9999, background: overlay.scrim, backdropFilter: 'blur(2px)' }}
      onClick={e => { if (e.target === e.currentTarget) onClose() }}>
      <div className="k-drawer"
        style={{ width: 'min(580px, 100vw)', animation: 'slide-in-right 220ms cubic-bezier(0.23,1,0.32,1)' }}>

        {/* Header */}
        <div className="flex items-center justify-between px-6 pt-6 pb-4 flex-shrink-0 border-b border-edge-row">
          <div>
            <div className="text-detail-title font-bold text-ink-primary">New Contact</div>
            <div className="text-12 text-ink-muted mt-1">Add a new contact to your CRM</div>
          </div>
          <button onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-control text-ink-faint hover:bg-surface-nav-hover transition-colors">
            <X size={16} />
          </button>
        </div>

        {/* Form */}
        <div className="flex-1 overflow-y-auto px-6 py-5 flex flex-col gap-5">

          {/* Name */}
          <Field label="Full Name *" icon={<User size={12} />}>
            <input type="text" value={name} onChange={e => setName(e.target.value)}
              placeholder="e.g. Sarah Connor" autoFocus className={inputCls} />
          </Field>

          {/* Email + Phone */}
          <div className="grid grid-cols-2 gap-4">
            <Field label="Email" icon={<Mail size={12} />}>
              <input type="email" value={email} onChange={e => { setEmail(e.target.value); setRefusal(null) }}
                onBlur={() => setMatchEmail(email.trim())}
                placeholder="sarah@company.com" className={inputCls} />
            </Field>
            <Field label="Phone" icon={<Phone size={12} />}>
              <input type="tel" value={phone} onChange={e => setPhone(e.target.value)}
                placeholder="+1 555 000 0000" className={inputCls} />
            </Field>
          </div>

          {/* C3b: the duplicate chooser — the SAME renderer as the convert modal
              (DuplicateChooser.tsx). The refusal form when the server said no;
              otherwise the advisory tile, once an email has been blurred. */}
          {refusal && (
            <RefusalChooser eyebrow="Contact not created — possible duplicate" message={refusal.message}
              hint="Found by the server when you submitted — not the advisory check. Open the existing contact, or create anyway."
              candidates={refusal.candidates} actionFor={actionFor}
              createAnywayNote="Creating anyway is recorded against the new contact."
              onCreateAnyway={() => handleSave(true)} createAnywayDisabled={busy || name.trim() === ''} />
          )}
          {!refusal && matchEmail !== '' && (
            <AdvisoryChooser loading={matchesQ.loading} error={matchesQ.error} candidates={candidates} actionFor={actionFor}
              copy={{
                checking: 'Checking for an existing contact with this email…',
                unavailable: 'Duplicate check unavailable — you can still create the contact.',
                empty: 'No existing contact uses this email.',
              }} />
          )}

          {/* Company + Title. §25 L-1/L-4: the picker with the near-match
              suggestion under it (AccountField); no create row here. */}
          <div className="grid grid-cols-2 gap-4">
            <Field label="Company" icon={<Building2 size={12} />}>
              <AccountField id="new-contact-company" value={account} onChange={setAccount}
                placeholder="Acme Corp" className={inputCls} />
            </Field>
            <Field label="Job Title" icon={<Briefcase size={12} />}>
              <input type="text" value={title} onChange={e => setTitle(e.target.value)}
                placeholder="VP of Sales" className={inputCls} />
            </Field>
          </div>

          {/* Location */}
          <Field label="Location" icon={<MapPin size={12} />}>
            <input type="text" value={location} onChange={e => setLocation(e.target.value)}
              placeholder="e.g. Leeds, UK" className={inputCls} />
          </Field>

          {/* Category */}
          <Field label="Category" icon={<Tag size={12} />}>
            <div className="flex items-center gap-2 flex-wrap">
              {CATEGORY_OPTIONS.map(o => {
                const active = category === o.value
                return (
                  <button key={o.value} type="button" onClick={() => setCategory(o.value)}
                    className={`px-4 py-2 rounded-full text-13 font-semibold border transition-all ${active ? 'border-transparent text-ink-on-accent' : 'border-edge-control bg-surface-control text-ink-secondary hover:border-ink-disabled'}`}
                    style={active ? { background: accent.DEFAULT } : {}}>
                    {o.label}
                  </button>
                )
              })}
            </div>
          </Field>

          {/* Prospect Segment */}
          <Field label="Prospect Segment" icon={<Layers size={12} />}>
            <div className="flex items-center gap-2 flex-wrap">
              {SEGMENT_OPTIONS.map(o => {
                const active = prospectSegment === o.value
                return (
                  <button key={o.value} type="button" onClick={() => setProspectSegment(o.value)}
                    className={`px-4 py-2 rounded-full text-13 font-semibold border transition-all ${active ? 'border-transparent text-ink-on-accent' : 'border-edge-control bg-surface-control text-ink-secondary hover:border-ink-disabled'}`}
                    style={active ? { background: accent.DEFAULT } : {}}>
                    {o.label}
                  </button>
                )
              })}
            </div>
          </Field>

          {/* LinkedIn */}
          <Field label="LinkedIn URL" icon={<Linkedin size={12} />}>
            <input type="url" value={linkedin} onChange={e => setLinkedin(e.target.value)}
              placeholder="https://linkedin.com/in/sarah-connor" className={inputCls} />
          </Field>

          {/* Notes */}
          <Field label="Notes" icon={<Link2 size={12} />}>
            <textarea
              placeholder="Add any notes about this contact..."
              rows={4}
              className={`${inputCls} resize-none`} />
          </Field>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between gap-3 px-6 pt-5 pb-14 border-t border-edge-row flex-shrink-0">
          <button onClick={onClose} className="k-btn-secondary">
            Cancel
          </button>
          <button onClick={() => handleSave(false)} disabled={busy} className="k-btn k-btn--lg disabled:opacity-60">
            {busy ? 'Creating…' : 'Create Contact'}
          </button>
        </div>
      </div>
    </div>,
    document.body
  )
}

// ── Contact Detail Drawer ─────────────────────────────────────────────────────

function ContactDetailDrawer({ contact, onClose, onSaved, onOpenAccount }: {
  contact: SalesContact
  onClose: () => void
  /** Slice E: the saved contact replaces the row in the list and this view. */
  onSaved: (updated: SalesContact) => void
  /** §25 L-7: the account chip opens the account page (the drawer closes). */
  onOpenAccount?: (accountId: string) => void
}) {
  useEffect(() => {
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }
    document.addEventListener('keydown', handler)
    return () => document.removeEventListener('keydown', handler)
  }, [onClose])

  const cfg = STATUS_CONFIG[contact.status]

  // Slice E (E-3): the drawer's OWN edit path, CONTACT-shaped copy — the lead
  // drawer stays lead-shaped and is not generalised. The rules (scope, the
  // changed-fields diff, SB-6's legacy-source handling) come from
  // contactEdit.ts; only the chrome lives here. The PUT carries the diff only.
  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState<ContactEditDraft | null>(null)
  const [saving, setSaving] = useState(false)
  const legacySource = (contact.source ?? '') !== '' && !SOURCE_OPTIONS.some(o => o.value === contact.source)
  const fieldCls = 'w-full px-3 py-2 bg-surface-control border border-edge-control rounded-control text-13 text-ink-body placeholder:text-ink-faint focus:outline-none focus:border-accent'
  const set = (patch: Partial<ContactEditDraft>) => setDraft(d => (d ? { ...d, ...patch } : d))

  async function save() {
    if (!draft) return
    const body = contactEditDiff(contact, draft)
    if (Object.keys(body).length === 0) return
    setSaving(true)
    try {
      const updated = await updateContact(contact.id, body)
      onSaved(updated)
      setEditing(false)
      setDraft(null)
      toast.success('Contact updated')
    } catch (e) {
      toast.error(apiErrorMessage(e, 'Failed to update contact'))
    } finally {
      setSaving(false)
    }
  }

  return createPortal(
    <div style={{ position: 'fixed', inset: 0, zIndex: 9999, background: overlay.scrim, backdropFilter: 'blur(2px)' }}
      onClick={e => { if (e.target === e.currentTarget) onClose() }}>
      <div className="k-drawer"
        style={{ width: 'min(520px, 100vw)', animation: 'slide-in-right 220ms cubic-bezier(0.23,1,0.32,1)' }}>

        {/* Header */}
        <div className="flex items-center justify-between px-6 pt-6 pb-4 flex-shrink-0 border-b border-edge-row">
          <div className="flex items-center gap-3">
            <Avatar name={contact.name} size={40} />
            <div>
              <div className="text-detail-title font-bold text-ink-primary">{contact.name}</div>
              <div className="text-12 text-ink-muted">{contact.title} · {contact.company}</div>
            </div>
          </div>
          <button onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-control text-ink-faint hover:bg-surface-nav-hover transition-colors">
            <X size={16} />
          </button>
        </div>

        {/* Status + stats */}
        <div className="flex items-center gap-3 px-6 py-4 border-b border-edge-row flex-shrink-0">
          <span className="k-badge" style={{ background: cfg.bg, color: cfg.text }}>
            <span className="k-badge-dot" />{cfg.label.toUpperCase()}
          </span>
          <div className="flex items-center gap-4 ml-2">
            <div className="text-center">
              <div className="text-16 font-bold text-ink-primary">{contact.deals ?? '—'}</div>
              <div className="text-overline text-ink-faint">Deals</div>
            </div>
            <div className="w-px h-8 bg-edge-row" />
            <div className="text-center">
              <div className="text-16 font-bold text-ink-primary">{contact.enriched ? '✓' : '—'}</div>
              <div className="text-overline text-ink-faint">Enriched</div>
            </div>
            <div className="w-px h-8 bg-edge-row" />
            <div className="text-center">
              <div className="text-13 font-semibold text-ink-primary">{contact.lastContacted}</div>
              <div className="text-overline text-ink-faint">Last Contact</div>
            </div>
          </div>
        </div>

        {/* Details */}
        <div className="flex-1 overflow-y-auto px-6 py-2">
          <div className="flex items-center justify-between mt-3 mb-1">
            <div className="k-eyebrow uppercase">Contact Info</div>
            {/* Slice E: the drawer's own edit — flips the rows below into inputs
                in place (the Account Detail precedent). Blank inputs keep their
                stored value; the PUT carries only what changed. */}
            {!editing ? (
              <button type="button" className="k-btn-quiet inline-flex items-center gap-1"
                onClick={() => { setDraft(draftFromContact(contact)); setEditing(true) }}>
                <Pencil size={12} /> Edit
              </button>
            ) : (
              <div className="flex items-center gap-2">
                <button type="button" className="k-btn-secondary" disabled={saving}
                  onClick={() => { setEditing(false); setDraft(null) }}>Cancel</button>
                <button type="button" className="k-btn disabled:opacity-60"
                  disabled={!draft || !contactEditSavable(contact, draft, saving)} onClick={() => void save()}>
                  {saving ? 'Saving…' : 'Save'}
                </button>
              </div>
            )}
          </div>
          {editing && draft ? (
            <div className="flex flex-col gap-3 py-2">
              <label className="block"><span className="text-11 text-ink-muted font-medium">Name *</span>
                <input value={draft.name} onChange={e => set({ name: e.target.value })} className={fieldCls} aria-label="Name" /></label>
              <div className="grid grid-cols-2 gap-3">
                <label className="block"><span className="text-11 text-ink-muted font-medium">Email</span>
                  <input type="email" value={draft.email} onChange={e => set({ email: e.target.value })} className={fieldCls} aria-label="Email" /></label>
                <label className="block"><span className="text-11 text-ink-muted font-medium">Phone</span>
                  <input type="tel" value={draft.phone} onChange={e => set({ phone: e.target.value })} className={fieldCls} aria-label="Phone" /></label>
              </div>
              <div className="grid grid-cols-2 gap-3">
                {/* §25 L-1: the picker — a pick links, "Unlink account" clears
                    (the diff sends null), edited text after a pick unlinks. */}
                <div className="block"><span className="text-11 text-ink-muted font-medium">Company</span>
                  <AccountField id="contact-edit-company" value={{ accountId: draft.accountId, company: draft.company }}
                    onChange={p => set({ accountId: p.accountId, company: p.company })} className={fieldCls} /></div>
                <label className="block"><span className="text-11 text-ink-muted font-medium">Title</span>
                  <input value={draft.title} onChange={e => set({ title: e.target.value })} className={fieldCls} aria-label="Title" /></label>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <label className="block"><span className="text-11 text-ink-muted font-medium">Location</span>
                  <input value={draft.location} onChange={e => set({ location: e.target.value })} className={fieldCls} aria-label="Location" /></label>
                <label className="block"><span className="text-11 text-ink-muted font-medium">Source</span>
                  <select value={draft.source} onChange={e => set({ source: e.target.value })} className={`${fieldCls} bg-surface-card`} aria-label="Source">
                    <option value="">{(contact.source ?? '') === '' ? 'No source' : '(keep current)'}</option>
                    {legacySource && <option value={contact.source}>{contact.source}</option>}
                    {SOURCE_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                  </select></label>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <label className="block"><span className="text-11 text-ink-muted font-medium">Category</span>
                  <select value={draft.category} onChange={e => set({ category: e.target.value })} className={`${fieldCls} bg-surface-card`} aria-label="Category">
                    <option value="">{(contact.category ?? '') === '' ? 'No category' : '(keep current)'}</option>
                    {CATEGORY_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                  </select></label>
                <label className="block"><span className="text-11 text-ink-muted font-medium">Segment</span>
                  <select value={draft.prospectSegment} onChange={e => set({ prospectSegment: e.target.value })} className={`${fieldCls} bg-surface-card`} aria-label="Segment">
                    <option value="">{(contact.prospectSegment ?? '') === '' ? 'No segment' : '(keep current)'}</option>
                    {SEGMENT_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                  </select></label>
              </div>
              <label className="block"><span className="text-11 text-ink-muted font-medium">Owner</span>
                <OwnerSelect value={draft.assignedEmployeeId} onChange={id => set({ assignedEmployeeId: id })} className={`${fieldCls} bg-surface-card`} /></label>
              <label className="block"><span className="text-11 text-ink-muted font-medium">Notes</span>
                <textarea rows={3} value={draft.notes} onChange={e => set({ notes: e.target.value })} className={`${fieldCls} resize-none`} aria-label="Notes" /></label>
              <div className="text-11 text-ink-faint">Blank fields keep their current value — clearing isn't supported yet, except the account link, which "Unlink account" clears.</div>
            </div>
          ) : (
            <>
          <InfoRow icon={<Mail size={14} />}     label="Email"   value={contact.email} />
          <InfoRow icon={<Phone size={14} />}    label="Phone"   value={contact.phone} />
          <InfoRow icon={<Building2 size={14} />} label="Company" value={contact.company} />
          {/* §25 L-7: the link beside the text it came with — a chip to the
              account page, or the honest Unlinked. */}
          <div className="flex items-start gap-3 py-3 border-b border-edge-row last:border-0">
            <div className="w-8 h-8 rounded-control bg-surface-input flex items-center justify-center text-ink-faint flex-shrink-0"><Link2 size={14} /></div>
            <div className="min-w-0">
              <div className="text-11 text-ink-muted font-medium mb-1">Account</div>
              <div className="text-13 font-medium truncate">
                <AccountLink contact={contact} size="text-13" withIcon onOpenAccount={onOpenAccount ? id => { onClose(); onOpenAccount(id) } : undefined} />
              </div>
            </div>
          </div>
          <InfoRow icon={<Briefcase size={14} />} label="Title"   value={contact.title} />
          <InfoRow icon={<MapPin size={14} />}   label="Location" value={contact.location || '—'} />
          <InfoRow icon={<Tag size={14} />}      label="Category" value={CATEGORY_OPTIONS.find(o => o.value === contact.category)?.label ?? (contact.category || '—')} />
          <InfoRow icon={<Layers size={14} />}   label="Segment"  value={SEGMENT_OPTIONS.find(o => o.value === contact.prospectSegment)?.label ?? (contact.prospectSegment || '—')} />
          <InfoRow icon={<Linkedin size={14} />} label="LinkedIn" value={contact.enriched ? `linkedin.com/in/${contact.name.toLowerCase().replace(' ', '-')}` : 'Not linked'} />
            </>
          )}

          <div className="k-eyebrow uppercase mt-5 mb-1">Activity</div>
          <div className="py-8 text-center text-13 text-ink-muted">
            No recent activity logged.<br />
            <span className="text-accent cursor-pointer hover:underline" onClick={onClose}>Log an activity</span> to get started.
          </div>

          {/* Slice B (0117): the audit strip. A contact has no stage, so this
              mount reads field history only. */}
          <div className="k-eyebrow uppercase mt-5 mb-1">Change history</div>
          <SalesAuditStrip recordType="contacts" recordId={contact.id} variant="bare" />
        </div>

        {/* Footer */}
        <div className="flex items-center gap-2 px-6 pt-5 pb-14 border-t border-edge-row flex-shrink-0">
          <button onClick={() => { toast.info('Email coming soon'); onClose() }}
            className="k-btn-secondary k-btn-secondary--flex flex-1 justify-center">
            <Mail size={13} /> Email
          </button>
          <button onClick={() => { toast.info('Call coming soon'); onClose() }}
            className="k-btn-secondary k-btn-secondary--flex flex-1 justify-center">
            <Phone size={13} /> Call
          </button>
          <button onClick={() => { toast.info('Log activity coming soon'); onClose() }}
            className="k-btn k-btn--lg flex-1 inline-flex items-center justify-center gap-2">
            <Plus size={13} /> Activity
          </button>
        </div>
      </div>
    </div>,
    document.body
  )
}

// ── Main page ─────────────────────────────────────────────────────────────────

/** Status has a natural pipeline order, so sort by that rank, not alphabet.
 *  lastContacted is a relative LABEL with no client timestamp, so it is not a
 *  sortable column — sorting the label text would reorder rows dishonestly. */
const STATUS_RANK: Record<SalesContact['status'], number> = { new: 0, contacted: 1, qualified: 2, unqualified: 3 }

function compareContacts(a: SalesContact, b: SalesContact, field: string): number {
  switch (field) {
    case 'name':    return a.name.localeCompare(b.name)
    case 'company': return a.company.localeCompare(b.company)
    case 'status':  return STATUS_RANK[a.status] - STATUS_RANK[b.status]
    // A contact with no count on the wire (pre-L-6 backend) sorts below a
    // real 0: absent is not the smallest number, it is not a number.
    case 'deals':   return (a.deals ?? -1) - (b.deals ?? -1)
    default:        return 0
  }
}

/** D3: what a row's two inline cells need from the page — the people list
 *  (read ONCE per page, never per row), which row has a write in flight, and
 *  the two commit hooks. Same-value picks never reach the hooks. */
interface InlineCellContext {
  people: PickOption[]
  peopleText: string
  busyId: string | null
  onStatus: (c: SalesContact, next: string) => void
  onOwner: (c: SalesContact, next: string) => void
  /** §25 L-7: the Company cell's account chip opens the account page. */
  onOpenAccount?: (accountId: string) => void
}

/** Body cell per CONTACTS_SCHEMA column id. The Name cell keeps the avatar +
 *  phone subline the page has always shown; the extra columns (phone as its own
 *  column, segment, category) are opt-in through the Columns menu. Status and
 *  Owner are the D3 inline picklists: the closed cell is the STORED value
 *  (badge / server-derived name), the menu commits on an option click. */
function contactCell(c: SalesContact, edit: InlineCellContext): Record<string, React.ReactNode> {
  return {
    name: (
      <div className="flex items-center gap-3">
        <Avatar name={c.name} size={28} />
        <div>
          {/* D3: the identity line never wraps — the Owner column joined the
              default set and the auto-layout table squeezes at 1440; the
              wrappable cells (company, title) absorb it instead. */}
          <div className="text-13 font-semibold text-ink-primary whitespace-nowrap">{c.name}</div>
          <div className="text-11 text-ink-faint">{c.phone}</div>
        </div>
      </div>
    ),
    // §25 L-7: the company text the contact carries, and under it the LINK —
    // the account chip or the honest "Unlinked" — where before the id
    // travelled to this row and rendered nowhere.
    company: (
      <div className="flex flex-col">
        <span className="text-13 text-ink-body">{c.company}</span>
        <AccountLink contact={c} onOpenAccount={edit.onOpenAccount} />
      </div>
    ),
    title: <span className="text-12 text-ink-secondary">{c.title}</span>,
    email: <span className="text-12 text-ink-secondary font-mono">{c.email}</span>,
    phone: <span className="text-12 text-ink-secondary">{c.phone || '—'}</span>,
    status: (
      <InlinePicklist value={c.status} options={CONTACT_STATUS_OPTIONS} label={`Change status for ${c.name}`}
        busy={edit.busyId === c.id} onCommit={v => edit.onStatus(c, v)}>
        <ContactStatusBadge status={c.status} />
      </InlinePicklist>
    ),
    owner: (
      <InlinePicklist value={c.assignedEmployeeId ?? ''} options={edit.people} emptyText={edit.peopleText}
        label={`Change owner for ${c.name}`} busy={edit.busyId === c.id} onCommit={v => edit.onOwner(c, v)}>
        <span className={`text-12 ${c.assignedTo ? 'text-ink-secondary' : 'text-ink-faint'}`}>{ownerCellText(c)}</span>
      </InlinePicklist>
    ),
    segment: <span className="text-12 text-ink-secondary">{SEGMENT_OPTIONS.find(o => o.value === c.prospectSegment)?.label ?? '—'}</span>,
    category: <span className="text-12 text-ink-secondary">{CATEGORY_OPTIONS.find(o => o.value === c.category)?.label ?? '—'}</span>,
    lastContacted: <span className="text-12 text-ink-faint">{c.lastContacted}</span>,
    linkedin: c.enriched ? (
      <div className="flex items-center gap-1 text-brand-linkedin"><Linkedin size={13} /><CheckCircle2 size={11} className="text-success" /></div>
    ) : (
      <div className="flex items-center gap-1 text-ink-disabled"><Linkedin size={13} /><Circle size={11} /></div>
    ),
    // §25 L-6: the server's count, any stage. null (no count on the wire)
    // renders the dash — a fabricated 0 was the defect this replaces.
    deals: c.deals == null
      ? <span className="text-12 text-ink-disabled">—</span>
      : <span className={`text-12 font-semibold ${c.deals > 0 ? 'text-accent' : 'text-ink-disabled'}`}>{c.deals}</span>,
  }
}

export function SalesContacts({ onOpenAccount }: {
  /** §25 L-7: the shell's account navigation (Dashboard.openAccount); absent
   *  in a host with no account page, where the link renders as text. */
  onOpenAccount?: (accountId: string) => void
} = {}) {
  const { data, loading, error } = useAsync(fetchContacts, [])
  const [contacts, setContacts] = useState<SalesContact[]>([])
  const [search, setSearch]     = useState('')
  const [selected, setSelected] = useState<Set<string>>(new Set())
  const [showNewDrawer, setShowNewDrawer]   = useState(false)
  const [detailContact, setDetailContact]   = useState<SalesContact | null>(null)
  // Saved views own the filters, the visible columns and the sort (D2). Search
  // stays local — it is a transient per-session lookup, not part of a view.
  const sv = useSavedViews(CONTACTS_SCHEMA)
  // D3: one directory read backs every row's owner picklist; busyId is the
  // row whose inline write is in flight (its two pickers are disabled).
  const directory = useAsync(() => fetchDirectory(), [])
  const [busyId, setBusyId] = useState<string | null>(null)

  // Seed local state from the fetched list (local state also holds optimistic adds).
  useEffect(() => { if (data) setContacts(data) }, [data])

  if (loading) return <LoadingState />
  if (error) return <ErrorState message={error} />

  const statusFilter = sv.state.filters.status ?? 'all'
  const segmentFilter = sv.state.filters.segment ?? 'all'
  const visibleCols = new Set(sv.state.columns)
  const shownColumns = CONTACTS_SCHEMA.columns.filter(c => visibleCols.has(c.id))
  const sort = sv.state.sort

  const filtered = contacts.filter(c => {
    if (statusFilter !== 'all' && c.status !== statusFilter) return false
    if (segmentFilter !== 'all' && c.prospectSegment !== segmentFilter) return false
    if (search && !c.name.toLowerCase().includes(search.toLowerCase()) &&
        !c.company.toLowerCase().includes(search.toLowerCase()) &&
        !c.email.toLowerCase().includes(search.toLowerCase())) return false
    return true
  })
  const rows = sort
    ? [...filtered].sort((a, b) => compareContacts(a, b, sort.field) * (sort.dir === 'desc' ? -1 : 1))
    : filtered

  const toggleSort = (field: string) => {
    if (!sort || sort.field !== field) sv.setSort({ field, dir: 'asc' as SortDir })
    else if (sort.dir === 'asc') sv.setSort({ field, dir: 'desc' as SortDir })
    else sv.setSort(null)
  }

  const toggleSelect = (id: string) => {
    setSelected(prev => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  const allSelected = rows.length > 0 && rows.every(c => selected.has(c.id))
  const toggleAll = () => {
    if (allSelected) setSelected(new Set())
    else setSelected(new Set(rows.map(c => c.id)))
  }

  function handleCreated(contact: SalesContact) {
    setContacts(prev => [contact, ...prev])
  }

  // D3 (D3.3): the inline commit. The PUT fires on the option click; until
  // the server answers the row keeps showing the STORED value with a
  // "Saving…" mark — nothing optimistic is ever drawn. On success the
  // server's record replaces the row (so the cell shows what was stored,
  // not what was clicked); on failure the row is untouched, because it never
  // showed anything else, and the toast carries the server's message.
  // D3.4: a status change can take the row out of the active view's filter;
  // the row LEAVES at once (the view stays true to its filter and the
  // Showing/chip counts stay honest) and the toast NAMES the departure, so it
  // is neither a silent vanish nor a row the filter is lying about.
  async function commitInline(c: SalesContact, field: 'status' | 'owner', body: { status: string } | { assignedEmployeeId: string }) {
    setBusyId(c.id)
    try {
      const updated = await updateContact(c.id, body)
      setContacts(prev => prev.map(x => (x.id === updated.id ? updated : x)))
      const from = field === 'status' ? statusLabel(c.status) : ownerLabel(c)
      const to = field === 'status' ? statusLabel(updated.status) : ownerLabel(updated)
      const left = !matchesContactsFilters(updated, sv.state.filters)
      toast.success(`${changeSummary(c.name, field, from, to)} · recorded in change history${left ? ' · no longer matches this view' : ''}`)
    } catch (e) {
      toast.error(apiErrorMessage(e, `${c.name}: ${field} not changed`))
    } finally {
      setBusyId(null)
    }
  }
  const inlineCtx: InlineCellContext = {
    people: ownerOptions(directory.data),
    peopleText: directory.loading ? 'Loading people…' : directory.error ? 'People unavailable — owner unchanged' : 'No current employees',
    busyId,
    onStatus: (c, next) => { const body = statusChange(c.status, next); if (body) void commitInline(c, 'status', body) },
    onOwner: (c, next) => { const body = ownerChange(c.assignedEmployeeId, next); if (body) void commitInline(c, 'owner', body) },
    onOpenAccount,
  }

  return (
    <div className="page-enter flex flex-col gap-4">
      <SectionTitle
        title="Contacts"
        subtitle={`${contacts.length} contacts · ${contacts.filter(c => c.enriched).length} enriched`}
        action={
          <div className="flex items-center gap-2">
            <button className="k-btn-secondary k-btn-secondary--flex" onClick={() => toast.info('Export coming soon')}>
              <Download size={12} /> Export
            </button>
            <button className="k-btn k-btn--lg inline-flex items-center gap-2" onClick={() => setShowNewDrawer(true)}>
              <Plus size={14} /> New Contact
            </button>
          </div>
        }
      />

      {/* Saved views + column picker (Slice D / D2) */}
      <SavedViewsBar sv={sv} schema={CONTACTS_SCHEMA} baseLabel="All contacts" />

      {/* Filter chips + search */}
      <div className="flex items-center gap-2 flex-wrap">
        {(['all', 'new', 'contacted', 'qualified', 'unqualified'] as const).map(s => {
          const count = s === 'all' ? contacts.length : contacts.filter(c => c.status === s).length
          return (
            <button key={s} onClick={() => sv.setFilter('status', s)}
              className={`px-3 py-2 rounded-full text-12 font-medium transition-colors capitalize flex items-center gap-2 ${statusFilter === s ? 'bg-ink-primary text-ink-on-accent border border-ink-primary' : 'bg-surface-control text-ink-secondary hover:bg-surface-nav-hover border border-edge-control hover:border-edge-control-hover'}`}>
              {s === 'all' ? 'All' : STATUS_CONFIG[s].label}
              <span className={`text-overline font-mono px-2 py-1 rounded-full ${statusFilter === s ? 'bg-accent text-ink-on-accent' : 'bg-surface-panel text-ink-faint'}`}>{count}</span>
            </button>
          )
        })}
        <div className="ml-auto relative">
          <Search size={12} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-ink-faint" />
          <input value={search} onChange={e => setSearch(e.target.value)}
            placeholder="Search name, company, email…"
            className="h-8 pl-7 pr-3 text-12 bg-surface-control border border-edge-control rounded-control text-ink-body placeholder:text-ink-faint focus:outline-none focus:border-accent w-56" />
        </div>
      </div>

      {/* Segment filter */}
      <div className="flex items-center gap-2 flex-wrap">
        <span className="k-eyebrow uppercase mr-1">Segment</span>
        {(['all', 'current', 'future'] as const).map(seg => {
          const count = seg === 'all' ? contacts.length : contacts.filter(c => c.prospectSegment === seg).length
          const label = seg === 'all' ? 'All' : SEGMENT_OPTIONS.find(o => o.value === seg)!.label
          return (
            <button key={seg} onClick={() => sv.setFilter('segment', seg)}
              className={`px-3 py-2 rounded-full text-12 font-medium transition-colors flex items-center gap-2 ${segmentFilter === seg ? 'bg-ink-primary text-ink-on-accent border border-ink-primary' : 'bg-surface-control text-ink-secondary hover:bg-surface-nav-hover border border-edge-control hover:border-edge-control-hover'}`}>
              {label}
              <span className={`text-overline font-mono px-2 py-1 rounded-full ${segmentFilter === seg ? 'bg-accent text-ink-on-accent' : 'bg-surface-panel text-ink-faint'}`}>{count}</span>
            </button>
          )
        })}
      </div>

      <div className="k-card">
        <table className="closr-table sales-table w-full">
          <thead>
            <tr>
              <th className="w-8 text-center">
                <input type="checkbox" checked={allSelected} onChange={toggleAll} className="rounded" />
              </th>
              {shownColumns.map(col => {
                const active = sort?.field === col.id
                return (
                  <th key={col.id} className="text-left">
                    {col.sortable ? (
                      <button type="button" onClick={() => toggleSort(col.id)}
                        className="inline-flex items-center gap-1 hover:text-ink-primary transition-colors"
                        aria-label={`Sort by ${col.label}`}>
                        {col.label}
                        {active
                          ? (sort!.dir === 'asc' ? <ChevronUp size={12} className="text-accent" /> : <ChevronDown size={12} className="text-accent" />)
                          : <ArrowUpDown size={11} className="text-ink-faint opacity-50" />}
                      </button>
                    ) : col.label}
                  </th>
                )
              })}
              <th className="w-8 cell-actions"></th>
            </tr>
          </thead>
          <tbody>
            {rows.map(c => {
              const cells = contactCell(c, inlineCtx)
              return (
                <tr key={c.id}
                  className={`cursor-pointer hover:bg-surface-row-hover transition-colors ${selected.has(c.id) ? 'bg-surface-panel' : ''}`}
                  onClick={() => setDetailContact(c)}>
                  <td className="text-center" onClick={e => e.stopPropagation()}>
                    <input type="checkbox" checked={selected.has(c.id)} onChange={() => toggleSelect(c.id)} className="rounded" />
                  </td>
                  {shownColumns.map(col => <td key={col.id}>{cells[col.id]}</td>)}
                  <td className="cell-actions" onClick={e => e.stopPropagation()}>
                    <button onClick={() => setDetailContact(c)}
                      className="p-1 rounded hover:bg-surface-nav-hover transition-colors">
                      <MoreHorizontal size={14} className="text-ink-faint" />
                    </button>
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>

        {/* The count line stays (honest); the old 1/2/3 page buttons were
            DEAD controls (no handler, no pagination exists) and are gone —
            a restyle must not re-ship a control that does nothing. */}
        <div className="flex items-center justify-between px-5 py-3 border-t border-edge-row">
          <span className="text-11 text-ink-faint">Showing {filtered.length} of {contacts.length} contacts</span>
        </div>
      </div>

      {/* Drawers */}
      {showNewDrawer && (
        <NewContactDrawer onClose={() => setShowNewDrawer(false)} onCreated={handleCreated}
          onOpenExisting={async id => {
            // C3b (B1.2): "Open existing contact" — close the create drawer
            // (nothing was created; the typed form is discarded, which is the
            // honest state) and open that contact's detail, fetched fresh so a
            // contact outside the loaded page still opens.
            setShowNewDrawer(false)
            try {
              setDetailContact(await fetchContact(id))
            } catch (e) {
              toast.error(apiErrorMessage(e, 'Could not open the existing contact'))
            }
          }} />
      )}
      {detailContact && (
        <ContactDetailDrawer contact={detailContact} onClose={() => setDetailContact(null)} onOpenAccount={onOpenAccount}
          onSaved={updated => {
            // Slice E: the saved contact replaces its row AND the open view —
            // never a stale copy of either.
            setContacts(prev => prev.map(c => (c.id === updated.id ? updated : c)))
            setDetailContact(updated)
          }} />
      )}
    </div>
  )
}
